import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Card from '@/components/common/Card';
import CommonTable, { type TableColumn } from '@/components/common/CommonTable';
import MainHeading from '@/components/common/MainHeading';
import Layout from '@/components/layout/Layout';
import CommonFilter from '@/components/common/CommonFilter';
import TablePagination from '@/components/common/TablePagination';
import { AttendanceService } from '@/services/Attendance/AttendanceService';
// import { GetFilterUrl } from '@/utility/helper';
import { ThrowError } from '@/api/callApi';
import type { IFilter, IPageInfo } from '@/interface/common.interface';
import type { IBulkAttendanceUser, IAttendanceDetail } from '@/interface/attendance.interface';
import type { shiftAndTime } from '@/interface/shiftAndTime.interface';
import { useLocation, useNavigate } from 'react-router-dom';
import moment from 'moment';
import { useAttendanceRules, type AttendanceStatus, type DayColumnInfo } from '@/hooks/useAttendanceRules';
import AttendanceDetailModal from './AttendanceDetailModal';
import { Download, RefreshCw, Users, Calendar, Sparkles, Info, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAppFilters } from '@/hooks/useAppFilters';
import { RoutesLink } from '@/utility/RouteLinks';

interface AttendanceRow {
    id: string;
    employeeName: string;
    empCode: string;
    avatar?: string;
    department: string;
    attendanceMap: Record<string, AttendanceStatus>;
    detailMap: Record<string, IAttendanceDetail>;
    shift?: shiftAndTime | null;
}

interface SelectedCellInfo {
    row: AttendanceRow;
    dayObj: DayColumnInfo;
    detail: IAttendanceDetail | null;
    status: AttendanceStatus;
}

// Styling mapping for dropdown statuses
const STATUS_OPTIONS: { label: string; value: AttendanceStatus }[] = [
    { label: '--', value: '--' },
    { label: 'P', value: 'PRESENT' },
    { label: 'A', value: 'ABSENT' },
    { label: 'P+MISSED', value: 'P+MISSED' },
    { label: 'WO', value: 'WO' },
    { label: 'WO+P', value: 'WO+P' },
    { label: 'H', value: 'HOLIDAY' },
    { label: 'H+P', value: 'HOLIDAY+P' },
    { label: 'F-D WFH', value: 'F-D WFH' },
    { label: 'F-D OD', value: 'F-D OD' },
    { label: 'WFH+P', value: 'WFH+P' },
    { label: 'P+WFH', value: 'P+WFH' },
    { label: 'OD+P', value: 'OD+P' },
    { label: 'P+OD', value: 'P+OD' },
    { label: 'L+P', value: 'L+P' },
    { label: 'P+L', value: 'P+L' },
    { label: 'L+WFH', value: 'L+WFH' },
    { label: 'WFH+L', value: 'WFH+L' },
    { label: 'L+OD', value: 'L+OD' },
    { label: 'OD+L', value: 'OD+L' },
    { label: 'WFH+OD+P', value: 'WFH+OD+P' },
    { label: 'HD', value: 'HALF_DAY' },
    { label: 'L', value: 'LEAVE' },
    { label: 'WFH', value: 'WFH' },
    { label: 'OD', value: 'OUT_DUTY' },
];

const STATUS_STYLE_MAP: Record<AttendanceStatus, string> = {
    '--': 'border-amber-300 text-amber-800 bg-amber-50/90 shadow-2xs hover:bg-amber-100 hover:border-amber-400',
    'PRESENT': 'border-emerald-400/80 text-emerald-800 bg-emerald-50/90 shadow-2xs hover:bg-emerald-100 hover:border-emerald-500 font-bold',
    'ABSENT': 'border-rose-300 text-rose-800 bg-rose-50/90 shadow-2xs hover:bg-rose-100 hover:border-rose-400 font-bold',
    'P+MISSED': 'border-yellow-400 text-yellow-950 bg-amber-100 font-black ring-1 ring-yellow-400/80 shadow-2xs hover:bg-amber-200',
    'WO': 'border-pink-300 text-pink-800 bg-pink-50/90 shadow-2xs hover:bg-pink-100 hover:border-pink-400 font-bold',
    'WO+P': 'border-teal-400 text-teal-900 bg-teal-100/90 font-black ring-1 ring-teal-400/80 shadow-2xs hover:bg-teal-200',
    'WO+HD': 'border-fuchsia-400 text-fuchsia-900 bg-fuchsia-100/90 font-black ring-1 ring-fuchsia-400/80 shadow-2xs hover:bg-fuchsia-200',
    'HOLIDAY': 'border-purple-300 text-purple-800 bg-purple-50/90 shadow-2xs hover:bg-purple-100 hover:border-purple-400 font-bold',
    'HOLIDAY+P': 'border-lime-400 text-lime-900 bg-lime-100/90 font-black ring-1 ring-lime-400/80 shadow-2xs hover:bg-lime-200',
    'HOLIDAY+HD': 'border-purple-400 text-purple-950 bg-purple-100/90 font-black ring-1 ring-purple-400/80 shadow-2xs hover:bg-purple-200',
    'F-D WFH': 'border-blue-400 text-blue-900 bg-blue-100/90 font-black ring-1 ring-blue-400/80 shadow-2xs hover:bg-blue-200',
    'F-D OD': 'border-orange-400 text-orange-900 bg-orange-100/90 font-black ring-1 ring-orange-400/80 shadow-2xs hover:bg-orange-200',
    'WFH+P': 'border-indigo-400 text-indigo-900 bg-indigo-100/90 font-black shadow-2xs hover:bg-indigo-200',
    'P+WFH': 'border-indigo-400 text-indigo-900 bg-indigo-100/90 font-black shadow-2xs hover:bg-indigo-200',
    'OD+P': 'border-amber-400 text-amber-950 bg-amber-100/90 font-black shadow-2xs hover:bg-amber-200',
    'P+OD': 'border-amber-400 text-amber-950 bg-amber-100/90 font-black shadow-2xs hover:bg-amber-200',
    'L+P': 'border-violet-400 text-violet-950 bg-violet-100/90 font-black shadow-2xs hover:bg-violet-200',
    'P+L': 'border-violet-400 text-violet-950 bg-violet-100/90 font-black shadow-2xs hover:bg-violet-200',
    'L+WFH': 'border-purple-400 text-purple-950 bg-purple-100/90 font-black shadow-2xs hover:bg-purple-200',
    'WFH+L': 'border-purple-400 text-purple-950 bg-purple-100/90 font-black shadow-2xs hover:bg-purple-200',
    'L+OD': 'border-orange-400 text-orange-950 bg-orange-100/90 font-black shadow-2xs hover:bg-orange-200',
    'OD+L': 'border-orange-400 text-orange-950 bg-orange-100/90 font-black shadow-2xs hover:bg-orange-200',
    'WFH+OD': 'border-cyan-400 text-cyan-950 bg-cyan-100/90 font-black ring-1 ring-cyan-400/80 shadow-2xs hover:bg-cyan-200',
    'WFH+OD+P': 'border-cyan-500 text-cyan-950 bg-cyan-100/90 font-black ring-1 ring-cyan-500 shadow-2xs hover:bg-cyan-200',
    'HALF_DAY': 'border-sky-300 text-sky-800 bg-sky-50/90 shadow-2xs hover:bg-sky-100 hover:border-sky-400 font-bold',
    'LEAVE': 'border-violet-300 text-violet-800 bg-violet-50/90 shadow-2xs hover:bg-violet-100 hover:border-violet-400 font-bold',
    'WFH': 'border-indigo-300 text-indigo-800 bg-indigo-50/90 shadow-2xs hover:bg-indigo-100 hover:border-indigo-400 font-bold',
    'OUT_DUTY': 'border-amber-300 text-amber-900 bg-amber-50/90 shadow-2xs hover:bg-amber-100 hover:border-amber-400 font-bold',
};

const LEGEND_ITEMS: { status: AttendanceStatus; label: string; desc: string }[] = [
    { status: '--', label: '--', desc: 'Pending' },
    { status: 'PRESENT', label: 'P', desc: 'Present' },
    { status: 'ABSENT', label: 'A', desc: 'Absent' },
    { status: 'HALF_DAY', label: 'HD', desc: 'Half Day' },
    { status: 'LEAVE', label: 'L', desc: 'Leave' },
    { status: 'WO', label: 'WO', desc: 'Weekly Off' },
    { status: 'HOLIDAY', label: 'H', desc: 'Holiday' },
    { status: 'WFH', label: 'WFH', desc: 'Work From Home' },
    { status: 'OUT_DUTY', label: 'OD', desc: 'Outdoor Duty' },
    { status: 'F-D WFH', label: 'F-D WFH', desc: 'Full Day WFH' },
    { status: 'F-D OD', label: 'F-D OD', desc: 'Full Day OD' },
    { status: 'P+MISSED', label: 'P+MISSED', desc: 'Missed Punch' },
    { status: 'WO+P', label: 'WO+P', desc: 'Week Off + Present' },
    { status: 'WO+HD', label: 'WO+HD', desc: 'Week Off + Half Day' },
    { status: 'HOLIDAY+P', label: 'H+P', desc: 'Holiday + Present' },
    { status: 'HOLIDAY+HD', label: 'H+HD', desc: 'Holiday + Half Day' },
    { status: 'WFH+P', label: 'WFH+P', desc: 'WFH + Present' },
    { status: 'P+WFH', label: 'P+WFH', desc: 'Present + WFH' },
    { status: 'OD+P', label: 'OD+P', desc: 'Outdoor Duty + Present' },
    { status: 'P+OD', label: 'P+OD', desc: 'Present + Outdoor Duty' },
    { status: 'L+P', label: 'L+P', desc: 'Leave + Present' },
    { status: 'P+L', label: 'P+L', desc: 'Present + Leave' },
    { status: 'L+WFH', label: 'L+WFH', desc: 'Leave + WFH' },
    { status: 'WFH+L', label: 'WFH+L', desc: 'WFH + Leave' },
    { status: 'L+OD', label: 'L+OD', desc: 'Leave + Outdoor Duty' },
    { status: 'OD+L', label: 'OD+L', desc: 'Outdoor Duty + Leave' },
    { status: 'WFH+OD', label: 'WFH+OD', desc: 'WFH + OD' },
    { status: 'WFH+OD+P', label: 'WFH+OD+P', desc: 'WFH + OD + Present' },
];

// Helper for Initials
const getInitials = (name: string) => {
    if (!name) return 'E';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
};

const getAvatarBg = (name: string) => {
    const gradients = [
        'from-blue-600 to-indigo-600',
        'from-purple-600 to-pink-600',
        'from-emerald-600 to-teal-600',
        'from-amber-500 to-orange-600',
        'from-rose-500 to-red-600',
        'from-cyan-600 to-blue-600',
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % gradients.length;
    return gradients[index];
};

const ManageMonthlyBulkAttendance: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const [paginationData, setPaginationData] = useState<IPageInfo>({
        currentPage: 1,
        pageSize: 10,
        total: 0,
        totalPages: 0,
    });

    const {
        isHQ,
        effectiveBranchId,
        effectiveSiteId,
        filterValues,
        filterSelectorsNode,
    } = useAppFilters({
        enabled: ['branch', 'site', 'department'],
        autoSelectFirst: { branch: true, site: true },
        onChange: () => {
            setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
        },
    });

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isExporting, setIsExporting] = useState<boolean>(false);
    const [tableData, setTableData] = useState<AttendanceRow[]>([]);
    const [selectedCellInfo, setSelectedCellInfo] = useState<SelectedCellInfo | null>(null);
    const [isLegendOpen, setIsLegendOpen] = useState<boolean>(true);

    // Hook for rule evaluations (Sunday check & multi-half combination rules)
    const { getDayColumns, getCalculatedStatus, applyRulesToAttendanceMap } = useAttendanceRules();

    // Export Excel Handler
    const handleExport = async () => {
        setIsExporting(true);
        try {
            const branchIdParam = effectiveBranchId ? `&branchId=${effectiveBranchId}` : '';
            const siteIdParam = effectiveSiteId ? `&siteId=${effectiveSiteId}` : '';
            const departmentIdParam = filterValues.departmentId ? `&departmentId=${filterValues.departmentId}` : '';
            const searchParam = filter.search ? `&search=${encodeURIComponent(filter.search)}` : '';
            const params = `startDate=${filter.startDate}&endDate=${filter.endDate}${branchIdParam}${siteIdParam}${departmentIdParam}${searchParam}`;
            const blob = await AttendanceService.downloadBulkAttendanceReport(params);

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute(
                'download',
                `bulk_attendance_${filter.startDate}_to_${filter.endDate}.xlsx`
            );
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Export failed:', error);
            ThrowError(error);
        } finally {
            setIsExporting(false);
        }
    };

    // Initial Dates calculation (matching MyAttendance logic)
    const getInitialDates = () => {
        if (location?.state?.startDate && location?.state?.endDate) {
            return {
                startDate: String(location.state.startDate),
                endDate: String(location.state.endDate),
            };
        }
        const start = moment().startOf('month').format('YYYY-MM-DD');
        const end = moment().endOf('month').format('YYYY-MM-DD');
        return { startDate: start, endDate: end };
    };

    const initialDates = getInitialDates();

    const [filter, setFilter] = useState<IFilter>({
        search: '',
        startDate: initialDates.startDate || '',
        endDate: initialDates.endDate || '',
    });

    // Dynamic Day Columns List with Date and Sunday Info
    const dayColumnsList = useMemo(() => {
        return getDayColumns(filter.startDate || '', filter.endDate || '');
    }, [filter.startDate, filter.endDate, getDayColumns]);

    // Fetch Bulk Attendance Data from API
    const fetchBulkAttendance = async (currentPage = paginationData.currentPage, currentLimit = paginationData.pageSize) => {
        // If Level 0 (HQ) and no branch/site selected yet, skip until loaded
        if (isHQ && !effectiveBranchId && !effectiveSiteId) return;

        setIsLoading(true);
        try {
            const branchIdParam = effectiveBranchId ? `&branchId=${effectiveBranchId}` : '';
            const siteIdParam = effectiveSiteId ? `&siteId=${effectiveSiteId}` : '';
            const departmentIdParam = filterValues.departmentId ? `&departmentId=${filterValues.departmentId}` : '';
            const searchParam = filter.search ? `&search=${encodeURIComponent(filter.search)}` : '';

            const PATH = `pageNumber=${currentPage}&pageSize=${currentLimit}&startDate=${filter.startDate}&endDate=${filter.endDate}${branchIdParam}${siteIdParam}${departmentIdParam}${searchParam}`;

            const response = await AttendanceService.getBulkAttendance(PATH);

            if (response?.status && response?.data) {
                const usersList: IBulkAttendanceUser[] = response.data.users || [];

                // Map users and their attendance array into clean row structure
                const rows: AttendanceRow[] = usersList.map((item, idx) => {
                    const user = item.user;
                    const rawAttendanceMap: Record<string, AttendanceStatus> = {};
                    const detailMap: Record<string, IAttendanceDetail> = {};

                    (item.attendance || []).forEach((attItem: IAttendanceDetail) => {
                        if (attItem.date) {
                            const dayStr = moment(attItem.date).format('DD');
                            rawAttendanceMap[dayStr] = (attItem.status as AttendanceStatus) || '--';
                            detailMap[dayStr] = attItem;
                        }
                    });

                    // Apply date-wise attendance rules (Sunday, WFH+P, OD+P, etc.)
                    const attendanceMap = applyRulesToAttendanceMap(rawAttendanceMap, dayColumnsList, detailMap);

                    return {
                        id: user?.id || String(idx + 1),
                        employeeName: user?.name || `Employee ${idx + 1}`,
                        empCode: user?.employeeCode || `EMP${idx + 1}`,
                        department: user?.department?.name || 'N/A',
                        attendanceMap,
                        detailMap,
                        shift: item.shift || null,
                    };
                });

                setTableData(rows);
                const pageInfo = response.data.pageInfo;
                setPaginationData((prev) => ({
                    ...prev,
                    total: pageInfo?.total || response.data.users?.length || 0,
                    totalPages: pageInfo?.totalPages || Math.ceil((pageInfo?.total || response.data.users?.length || 0) / currentLimit),
                }));
            }
        } catch (error) {
            console.error('Error fetching bulk attendance:', error);
            ThrowError(error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleChangePageSize = (size: number) => {
        setPaginationData((prev) => ({
            ...prev,
            pageSize: size,
            currentPage: 1,
        }));
    };

    useEffect(() => {
        fetchBulkAttendance(paginationData.currentPage, paginationData.pageSize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationData.currentPage, paginationData.pageSize, filter.search, filter.startDate, filter.endDate, effectiveBranchId, effectiveSiteId, filterValues.departmentId]);

    // Handlers
    const handleDateRangeChange = useCallback((start: string, end: string) => {
        setFilter((prev) => ({ ...prev, startDate: start, endDate: end }));
        setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
    }, []);

    const handleSearchChange = useCallback((value: string) => {
        setFilter((prev) => ({ ...prev, search: value }));
        setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
    }, []);

    const todayStr = useMemo(() => moment().format('YYYY-MM-DD'), []);

    // CommonTable Columns Construction
    const columns: TableColumn<AttendanceRow>[] = useMemo(() => {
        const fixedCols: TableColumn<AttendanceRow>[] = [
            {
                key: 'srNo',
                header: '#',
                headerClassName: 'text-center border-r border-slate-200 bg-slate-100/90 font-black text-slate-700 shadow-2xs',
                className: 'w-12 text-center font-bold text-slate-500 border-r border-slate-200/80 bg-slate-50/50',
                render: (_, index) => (
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-slate-200/60 text-[11px] font-bold text-slate-600">
                        {(paginationData.currentPage - 1) * paginationData.pageSize + index + 1}
                    </span>
                ),
            },
            {
                key: 'employee',
                header: 'Employee Details',
                headerClassName: 'border-r border-slate-200 bg-slate-100/90 font-black text-slate-700 shadow-2xs',
                className: 'min-w-[210px] max-w-[240px] border-r border-slate-200/80 bg-white p-2.5',
                render: (record) => (
                    <div className="flex items-center space-x-3 min-w-0">
                        <div className={`w-8 h-8 rounded-full bg-linear-to-tr ${getAvatarBg(record.employeeName)} text-white flex items-center justify-center font-extrabold text-xs shadow-xs shrink-0 ring-2 ring-white`}>
                            {getInitials(record.employeeName)}
                        </div>
                        <div className="flex flex-col min-w-0">
                            <span onClick={() => navigate(`${RoutesLink.employeeDetail}/${record?.id}`)} className="font-bold text-slate-800 text-xs truncate leading-snug cursor-pointer" title={record.employeeName}>
                                {record.employeeName}
                            </span>
                            <span className="text-[10px] text-slate-500 font-semibold truncate">
                                {record.department}
                            </span>
                        </div>
                    </div>
                ),
            },
            {
                key: 'empCode',
                header: 'Emp Code',
                headerClassName: 'text-center border-r border-slate-200 bg-slate-100/90 font-black text-slate-700 shadow-2xs',
                className: 'w-28 text-center font-bold text-slate-700 border-r border-slate-200/80 bg-white',
                render: (record) => (
                    <span className="inline-block px-2.5 py-1 rounded-md bg-slate-100 text-slate-700 font-mono font-bold text-[11px] border border-slate-200/80">
                        {record.empCode}
                    </span>
                ),
            },
        ];

        const dayCols: TableColumn<AttendanceRow>[] = dayColumnsList.map((dayObj) => {
            const isToday = dayObj.fullDate === todayStr;

            return {
                key: dayObj.dayStr,
                header: (
                    <div className="flex flex-col items-center justify-center py-1">
                        <span className={`text-[10px] uppercase font-bold tracking-wider ${dayObj.isSunday ? 'text-rose-600' : isToday ? 'text-indigo-600' : 'text-slate-500'
                            }`}>
                            {dayObj.dayName}
                        </span>
                        <span className={`text-xs font-black mt-0.5 px-1.5 py-0.2 rounded ${isToday
                            ? 'bg-indigo-600 text-white shadow-2xs'
                            : dayObj.isSunday
                                ? 'text-rose-600 font-extrabold'
                                : 'text-slate-800'
                            }`}>
                            {dayObj.dayStr}
                        </span>
                    </div>
                ),
                headerClassName: `text-center font-extrabold border-r border-slate-200 min-w-[56px] px-1 py-1.5 transition-colors ${isToday
                    ? 'bg-indigo-50/90 text-indigo-900 border-b-2 border-b-indigo-600'
                    : dayObj.isSunday
                        ? 'bg-rose-50/90 text-rose-700'
                        : 'bg-slate-100/90 text-slate-700'
                    }`,
                className: `text-center min-w-[56px] border-r border-slate-200/70 p-1.5 ${isToday
                    ? 'bg-indigo-50/30'
                    : dayObj.isSunday
                        ? 'bg-rose-50/30'
                        : ''
                    }`,
                render: (record) => {
                    const rawStatus = record.attendanceMap[dayObj.dayStr] || '--';
                    const detail = record.detailMap?.[dayObj.dayStr] || null;
                    const currentStatus = getCalculatedStatus(dayObj.fullDate, rawStatus, detail);
                    const style = STATUS_STYLE_MAP[currentStatus] || STATUS_STYLE_MAP['--'];

                    const displayLabel = STATUS_OPTIONS.find((opt) => opt.value === currentStatus)?.label || currentStatus;
                    const isFutureDate = moment(dayObj.fullDate, 'YYYY-MM-DD').isAfter(moment(), 'day');

                    return (
                        <button
                            onClick={() => {
                                if (isFutureDate) return;
                                setSelectedCellInfo({ row: record, dayObj, detail, status: currentStatus });
                            }}
                            disabled={isFutureDate}
                            className={`w-full py-1.5 px-1 text-center text-[11px] font-black rounded-lg border ${style} ${isFutureDate
                                ? 'opacity-40 cursor-not-allowed shadow-none hover:scale-100'
                                : 'cursor-pointer transition-all duration-150 hover:scale-105 hover:z-10 hover:shadow-md active:scale-95'
                                } flex items-center justify-center`}
                            title={isFutureDate ? `Future date (${dayObj.fullDate}) - Modal disabled` : `Click to view/edit details for ${record.employeeName} (${dayObj.fullDate})`}
                        >
                            {displayLabel}
                        </button>
                    );
                },
            };
        });

        return [...fixedCols, ...dayCols];
    }, [dayColumnsList, getCalculatedStatus, paginationData.currentPage, paginationData.pageSize, todayStr]);

    return (
        <Layout>
            <div className="space-y-6">
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <MainHeading title="Manage Monthly Bulk Attendance" />
                    <Button
                        variant="outline"
                        className="bg-white border-slate-200 text-slate-700 hover:bg-slate-50 font-semibold shadow-2xs cursor-pointer"
                        onClick={handleExport}
                        disabled={isExporting}
                    >
                        {isExporting ? (
                            <RefreshCw size={16} className="mr-2 animate-spin text-slate-500" />
                        ) : (
                            <Download size={16} className="mr-2 text-slate-600" />
                        )}
                        Export Excel
                    </Button>
                </div>

                {/* Summary KPI Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-white p-4 rounded-xl border border-slate-200/80 shadow-xs flex items-center space-x-3.5">
                        <div className="p-3 rounded-xl bg-indigo-50 text-indigo-600 border border-indigo-100 shrink-0">
                            <Users className="w-5 h-5" />
                        </div>
                        <div>
                            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Total Employees</p>
                            <p className="text-lg font-black text-slate-900 leading-snug">{paginationData.total || tableData.length}</p>
                        </div>
                    </div>

                    <div className="bg-white p-4 rounded-xl border border-slate-200/80 shadow-xs flex items-center space-x-3.5">
                        <div className="p-3 rounded-xl bg-blue-50 text-blue-600 border border-blue-100 shrink-0">
                            <Calendar className="w-5 h-5" />
                        </div>
                        <div>
                            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Selected Period</p>
                            <p className="text-xs font-extrabold text-slate-800 leading-snug">
                                {moment(filter.startDate).format('MMM DD')} - {moment(filter.endDate).format('MMM DD, YYYY')}
                            </p>
                        </div>
                    </div>

                    <div className="bg-white p-4 rounded-xl border border-slate-200/80 shadow-xs flex items-center space-x-3.5">
                        <div className="p-3 rounded-xl bg-purple-50 text-purple-600 border border-purple-100 shrink-0">
                            <Sparkles className="w-5 h-5" />
                        </div>
                        <div>
                            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Total Days in Period</p>
                            <p className="text-lg font-black text-slate-900 leading-snug">{dayColumnsList.length} Days</p>
                        </div>
                    </div>

                    <div className="bg-white p-4 rounded-xl border border-slate-200/80 shadow-xs flex items-center space-x-3.5">
                        <div className="p-3 rounded-xl bg-rose-50 text-rose-600 border border-rose-100 shrink-0">
                            <Info className="w-5 h-5" />
                        </div>
                        <div>
                            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Weekly Offs (Sundays)</p>
                            <p className="text-lg font-black text-slate-900 leading-snug">
                                {dayColumnsList.filter(d => d.isSunday).length} Days
                            </p>
                        </div>
                    </div>
                </div>

                <Card className="p-6 space-y-6 shadow-xl border-slate-200/80 rounded-2xl bg-white">


                    {/* Color Guide / Legend Card */}
                    <div className="rounded-xl border border-slate-200 bg-linear-to-r from-slate-50/90 to-indigo-50/30 overflow-hidden shadow-2xs transition-all">
                        <button
                            type="button"
                            onClick={() => setIsLegendOpen(!isLegendOpen)}
                            className="w-full flex items-center justify-between p-3.5 text-left text-xs font-black text-slate-800 cursor-pointer hover:bg-slate-100/50 transition-colors"
                        >
                            <div className="flex items-center space-x-2">
                                <Sparkles className="w-4 h-4 text-indigo-600" />
                                <span>Attendance Status Legend & Color Codes</span>
                                <span className="text-[10px] font-bold text-indigo-700 bg-indigo-100/80 px-2 py-0.5 rounded-full border border-indigo-200">
                                    Click badges in table to view details
                                </span>
                            </div>
                            {isLegendOpen ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
                        </button>

                        {isLegendOpen && (
                            <div className="p-3.5 border-t border-slate-200/80 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 text-[11px]">
                                {LEGEND_ITEMS.map((item) => {
                                    const style = STATUS_STYLE_MAP[item.status] || STATUS_STYLE_MAP['--'];
                                    return (
                                        <div
                                            key={item.status}
                                            className={`flex items-center justify-between px-2.5 py-1.5 rounded-lg border ${style} transition-all duration-150 hover:scale-[1.02] shadow-2xs`}
                                            title={`${item.status}: ${item.desc}`}
                                        >
                                            <span className="font-black text-xs">{item.label}</span>
                                            <span className="text-[10px] font-medium opacity-90 truncate ml-1">({item.desc})</span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    {/* CommonFilter: Search, DateRangePicker (startDate & endDate) */}
                    <CommonFilter
                        searchValue={filter.search}
                        onSearchChange={handleSearchChange}
                        searchPlaceholder="Search employee name or code..."
                        startDate={filter.startDate}
                        endDate={filter.endDate}
                        onDateRangeChange={handleDateRangeChange}
                        additionalFilters={filterSelectorsNode}
                    />
                    {/* CommonTable render with Grid Borders & Sticky Left Columns */}
                    <div className="w-full overflow-x-auto border border-slate-200 rounded-xl shadow-sm bg-white">
                        <CommonTable
                            columns={columns}
                            data={tableData}
                            loading={isLoading}
                            tableClassName="border-collapse divide-x divide-slate-200"
                            stickyColumns={3}
                        />
                    </div>

                    {/* Table Pagination - Exactly matching MyAttendance.tsx */}
                    <TablePagination
                        paginationData={paginationData}
                        setPaginationData={setPaginationData}
                        handleChangePageSize={handleChangePageSize}
                        isLoading={isLoading}
                    />
                </Card>

                {/* Attendance Details Modal */}
                {selectedCellInfo && (
                    <AttendanceDetailModal
                        isOpen={Boolean(selectedCellInfo)}
                        onClose={() => setSelectedCellInfo(null)}
                        employeeName={selectedCellInfo.row.employeeName}
                        empCode={selectedCellInfo.row.empCode}
                        department={selectedCellInfo.row.department}
                        fullDate={selectedCellInfo.dayObj.fullDate}
                        dayName={selectedCellInfo.dayObj.dayName}
                        isSunday={selectedCellInfo.dayObj.isSunday}
                        status={selectedCellInfo.status}
                        detail={selectedCellInfo.detail}
                        shift={selectedCellInfo.row.shift}
                        userId={selectedCellInfo.row.id}
                        onSuccess={() => fetchBulkAttendance()}
                    />
                )}
            </div>
        </Layout>
    );
};

export default ManageMonthlyBulkAttendance;